import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import './Login.css';

const dashboardFor = (user) => ({ employee: '/employee/calendar', customer: '/customer/dashboard', admin: '/admin/dashboard', support: '/support' }[user?.userType] || '/login');

const LinkLogin = () => {
  const { loginWithLink, verifyLoginLink, currentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Keep credentials only in component memory, never local/session storage.
  // An annual link can be retried after transient failure without being lost
  // when its address-bar fragment is scrubbed.
  const [credential] = useState(() => {
    const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
    const verification = fragment.get('verification');
    return { verification: !!verification, token: verification || fragment.get('token') || new URLSearchParams(location.search).get('token') };
  });
  const [state, setState] = useState(credential.verification ? 'confirm' : credential.token ? 'pending' : 'missing');
  const [failure, setFailure] = useState(null);
  const started = useRef(false);
  const pending = useRef(false);
  const active = useRef(true);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const exchange = useCallback(async () => {
    if (pending.current || !credential.token) return;
    pending.current = true;
    setState('pending');
    setFailure(null);
    try {
      const user = await (credential.verification ? verifyLoginLink : loginWithLink)(credential.token);
      if (active.current) navigate(dashboardFor(user), { replace: true });
    } catch (error) {
      if (!active.current) return;
      setFailure(error);
      setState('failed');
    } finally {
      pending.current = false;
    }
  }, [credential, loginWithLink, verifyLoginLink, navigate]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    window.history.replaceState(window.history.state, '', '/link-login');
    // Annual invitation links remain one-click. Mailbox-verification links are
    // consumed only by the explicit button below, not a preview/scanner load.
    if (credential.token && !credential.verification) exchange();
  }, [credential, exchange]);

  useEffect(() => {
    if (!credential.token && currentUser) navigate(dashboardFor(currentUser), { replace: true });
  }, [credential.token, currentUser, navigate]);

  if (state === 'confirm') return <div className="login-container">
    <h1 className="login-title">{t('auth.access.open')}</h1>
    <p className="login-explanation">{t('auth.access.confirm')}</p>
    <button type="button" className="submit-button" onClick={exchange}>{t('auth.access.open')}</button>
  </div>;

  if (state === 'pending') return <div className="login-container" role="status" aria-live="polite">
    <h1 className="login-title">{t('auth.linkLogin.signingIn')}</h1>
    <p className="login-explanation">{t('auth.linkLogin.wait')}</p>
  </div>;

  const code = failure?.code;
  const temporary = state === 'failed' && (!failure?.status || failure.status >= 500 || failure.status === 429);
  let messageKey = 'auth.access.invalidLink';
  if (state === 'missing') messageKey = 'auth.access.missingLink';
  else if (code === 'LINK_DEVICE_LIMIT') messageKey = 'auth.access.deviceLimit';
  else if (code === 'LINK_DEVICE_SETUP_REQUIRED') messageKey = 'auth.access.cookiesRequired';
  else if (code === 'LINK_ACCOUNT_INACTIVE') messageKey = 'auth.access.inactive';
  else if (code === 'INVALID_LOGIN_VERIFICATION') messageKey = 'auth.access.invalidVerification';
  else if (failure?.status === 429) messageKey = 'auth.access.rateLimited';
  else if (temporary) messageKey = 'auth.access.temporaryFailure';

  return <div className="login-container">
    <h1 className="login-title">{t('auth.linkLogin.failedTitle')}</h1>
    <p className="login-explanation" role="alert">{t(messageKey)}</p>
    {temporary && <button type="button" className="submit-button" onClick={exchange}>{t('common.retry')}</button>}
    <Link to="/login" className="forgot-password-link">{t('auth.access.send')}</Link>
  </div>;
};

export default LinkLogin;
